import { useState, useEffect, useMemo } from 'react';
import { Combobox, type ComboboxOption } from '../ui/Combobox';
import { getLocationService } from '../../services/locationServiceInstance';
import type { City, CityBasic, State } from '../../types/location';

export interface CitySelectorProps {
  readonly country: { id: string; name: string; iso2: string } | null;
  readonly state: State | null;
  readonly value?: Pick<City, 'id' | 'name' | 'latitude' | 'longitude'> | null;
  readonly onChange: (city: City | null) => void;
  readonly onStateResolved?: (state: State, city: City) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: string;
  readonly className?: string;
  readonly showCoordinates?: boolean;
}

/**
 * CitySelector Component
 * 
 * Two modes:
 * 1. Country + State → loads cities for that state (1 call)
 * 2. Country only → loads all cities for country (1 call) + states (1 call).
 *    On city selection, auto-resolves state from city.stateCode (instant, no extra calls).
 */
export function CitySelector({
  country,
  state,
  value,
  onChange,
  onStateResolved,
  disabled = false,
  required = false,
  label = 'Ciudad',
  placeholder = 'Seleccione una ciudad',
  error,
  className = '',
  showCoordinates = false
}: CitySelectorProps) {
  const [citiesByState, setCitiesByState] = useState<City[]>([]);
  const [citiesByCountry, setCitiesByCountry] = useState<CityBasic[]>([]);
  const [statesCache, setStatesCache] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [stateWasAutoResolved, setStateWasAutoResolved] = useState(false);

  const mode = state && !stateWasAutoResolved ? 'byState' : 'byCountry';

  useEffect(() => {
    setStateWasAutoResolved(false);
  }, [country?.iso2]);

  useEffect(() => {
    if (!country) {
      setCitiesByState([]);
      setCitiesByCountry([]);
      return;
    }
    if (stateWasAutoResolved) return;

    if (state) {
      loadCitiesByState(country.iso2, state.iso2);
    } else {
      loadCitiesByCountry(country.iso2);
    }
  }, [country?.iso2, state?.iso2, stateWasAutoResolved]);

  const loadCitiesByState = async (countryIso2: string, stateIso2: string) => {
    setLoading(true);
    setErrorState(null);
    try {
      const service = getLocationService();
      const data = await service.getCities(countryIso2, stateIso2);
      setCitiesByState(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar ciudades';
      setErrorState(message);
    } finally {
      setLoading(false);
    }
  };

  const loadCitiesByCountry = async (countryIso2: string) => {
    setLoading(true);
    setErrorState(null);
    try {
      const service = getLocationService();
      const [cities, states] = await Promise.all([
        service.getCitiesByCountry(countryIso2),
        service.getStates(countryIso2)
      ]);
      setCitiesByCountry(cities);
      setStatesCache(states);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar ciudades';
      setErrorState(message);
    } finally {
      setLoading(false);
    }
  };

  // Build a stateCode → stateName map for quick lookup
  const stateNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of statesCache) {
      map.set(s.iso2, s.name);
    }
    return map;
  }, [statesCache]);

  const options: ComboboxOption[] = useMemo(() => {
    if (mode === 'byState') {
      return [...citiesByState]
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
        .map(city => ({
          value: city.id,
          label: city.name,
          subtitle: showCoordinates && city.latitude && city.longitude
            ? `📍 ${city.latitude.toFixed(4)}, ${city.longitude.toFixed(4)}`
            : undefined
        }));
    }

    return [...citiesByCountry]
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
      .map(city => ({
        value: city.id,
        label: city.name,
        subtitle: city.stateCode ? stateNameMap.get(city.stateCode) : undefined
      }));
  }, [mode, citiesByState, citiesByCountry, showCoordinates, stateNameMap]);

  const handleChange = (selectedId: string) => {
    if (!selectedId) {
      onChange(null);
      setStateWasAutoResolved(false);
      return;
    }

    if (mode === 'byState') {
      const city = citiesByState.find(c => c.id === selectedId);
      if (city) onChange(city);
      return;
    }

    // byCountry mode: resolve state instantly from stateCode
    const cityBasic = citiesByCountry.find(c => c.id === selectedId);
    if (!cityBasic || !country) return;

    const city: City = {
      id: cityBasic.id,
      name: cityBasic.name,
      stateId: '',
      stateCode: cityBasic.stateCode || '',
      countryId: country.id,
      countryCode: country.iso2,
      latitude: cityBasic.latitude,
      longitude: cityBasic.longitude
    };

    // Auto-resolve state from stateCode (instant lookup, no API call)
    if (onStateResolved && cityBasic.stateCode) {
      const resolvedState = statesCache.find(s => s.iso2 === cityBasic.stateCode);
      if (resolvedState) {
        setStateWasAutoResolved(true);
        onStateResolved(resolvedState, city);
        return;
      }
    }

    onChange(city);
  };

  const isDisabled = disabled || !country || loading;
  const displayError = error || errorState;

  const getPlaceholderText = () => {
    if (!country) return 'Primero seleccione un país';
    if (loading) return 'Cargando ciudades...';
    const count = mode === 'byState' ? citiesByState.length : citiesByCountry.length;
    if (count === 0) return 'No hay ciudades disponibles';
    return placeholder;
  };

  return (
    <div className={className}>
      <Combobox
        options={options}
        value={value?.id || ''}
        onChange={handleChange}
        label={label}
        placeholder={getPlaceholderText()}
        required={required}
        disabled={isDisabled}
        error={displayError || undefined}
        emptyMessage={country ? 'No se encontraron ciudades' : 'Primero seleccione un país'}
        allowClear={true}
      />

      {value && showCoordinates && value.latitude && value.longitude && (
        <div className="text-muted small mt-1">
          📍 Coordenadas: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
        </div>
      )}
    </div>
  );
}
